import React, {type ReactNode, useCallback, useState} from "react";
import styles from "./ConstructionTableCollapsible.module.scss";
import {IconButton} from "@fluentui/react";
import IsaacBadge from "../../../../components/isaacBadge/IsaacBadge";
import {addToLocaLStorage, getFromLocalStorage} from "../../../../utils/ManageLocalStorage";
import IsaacTranslatedText from "../../../../components/isaacTranslatedText/IsaacTranslatedText";

interface ConstructionTableCollapsibleProps {
  count: number;
  name: string;
  children: ReactNode;
}

const ICON_CHEVRON_DOWN = "ChevronDown";
const ICON_CHEVRON_UP = "ChevronUp";
const ICON_TITLE_SHOW = "Show";
const ICON_TITLE_HIDE = "Hide";
const LOCAL_STORAGE_KEY = "hiddenTables";
const DEFAULT_HIDDEN: string[] = ["mastCoordinates"];

const getHiddenTables = (): string[] => getFromLocalStorage<string[]>(LOCAL_STORAGE_KEY) ?? DEFAULT_HIDDEN;
const setHiddenTables = (tables: string[]): void => {
  addToLocaLStorage(LOCAL_STORAGE_KEY, tables);
};

const ConstructionTableCollapsible: React.FC<ConstructionTableCollapsibleProps> = ({children, name, count}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(getHiddenTables().includes(name));

  const handleToggle = useCallback(() => {
    setIsCollapsed(prev => {
      const hidden = getHiddenTables();
      const isNowCollapsed = !prev;
      let updated: string[];
      if (isNowCollapsed) {
        updated = [...hidden, name];
      } else {
        updated = hidden.filter(t => t !== name);
      }
      setHiddenTables(updated);
      return isNowCollapsed;
    });
  }, [name]);

  const iconName = isCollapsed ? ICON_CHEVRON_DOWN : ICON_CHEVRON_UP;
  const iconTitle = isCollapsed ? ICON_TITLE_SHOW : ICON_TITLE_HIDE;

  if (count === 0) {
    return null;
  }

  return (
    <div className={styles.collapsibleContainer}>
      <div className={styles.collapsibleHeader}>
        <div className={styles.title}>
          <IconButton iconProps={{iconName}} title={iconTitle} ariaLabel="Collapse" onClick={handleToggle} />
          <IsaacTranslatedText parent={name} />
          <IsaacBadge title="Count" className={styles.total} isVisible={count !== undefined}>
            {count}
          </IsaacBadge>
        </div>
      </div>
      {!isCollapsed && <div>{children}</div>}
    </div>
  );
};

export default ConstructionTableCollapsible;
